import { InfoMessage } from '@/components/shared/InfoMessage/InfoMessage';
import { Button } from '@/components/ui/Button/Button';
import { DialogBody, DialogFooter } from '@/components/ui/Dialog/Dialog';
import { toast } from '@/components/ui/Toast/Toast';
import { captureError } from '@/config/sentry';
import { useRescheduleAppointment } from '@/features/scheduling/api/rescheduleAppointment';
import { useRescheduleRecurringAppointment } from '@/features/scheduling/api/rescheduleRecurringAppointment';
import { getStudentEnrollmentDetails } from '@/features/scheduling/utils/getStudentEnrollmentDetails';
import { invalidateSchedulingQueries } from '@/features/scheduling/utils/invalidateSchedulingQueries';
import { useTimeFor } from '@/hooks/useTimeFor';
import { useErrorTranslation } from '@/hooks/useErrorTranslation';
import type { ApiErrorResponse } from '@/lib/apiClient';
import { captureAnalyticsEvent } from '@/lib/mixpanel';
import type { Appointment } from '@/types/scheduling';
import { centerDayFromKey, toUtcIsoString } from '@/utils/timezone';
import { endOfDay } from 'date-fns';
import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SessionSummary } from '../../../SessionSummary/SessionSummary';
import { useRescheduleFlow } from '../../context/useRescheduleFlow';

export const SummaryStep = () => {
  const { t } = useTranslation('scheduling', { keyPrefix: 'rescheduleDialog.steps.summaryStep' });
  const { translateError } = useErrorTranslation();
  const { timeZone: centerTimeZone, formatDate } = useTimeFor('sessionTime');
  const { data, goToPreviousStep, goToNextStep, goToStep, updateData, setErrorType } = useRescheduleFlow();

  if (!data.appointment) {
    throw new Error('Appointment data is required for SummaryStep');
  }

  const isRecurring = data.appointment?.recurring_schedule && data.applyToFollowingSessions;

  // `recurring.endDate` is a bare center day, so the API and the summary card both get the last
  // moment of that day in the center's zone.
  const recurrenceEndsAt = data.recurring.endDate ? toUtcIsoString(endOfDay(centerDayFromKey(data.recurring.endDate, centerTimeZone))) : undefined;

  const updatedAppointment: Appointment = {
    ...data.appointment,
    appointment_date: data.newDateAndTime ?? data.appointment.appointment_date,
    recurring_schedule: isRecurring
      ? {
          ...data.appointment.recurring_schedule!,
          recurrence_pattern: data.recurring.recurrencePattern!,
          effective_until: recurrenceEndsAt!,
        }
      : undefined,
  };

  const handleErrors = (error: ApiErrorResponse) => {
    if (error.errors?.some((e) => e.code === 'APPOINTMENT_RESCHEDULE_IS_LATE')) {
      goToStep('lateReschedule');
      return;
    }

    if (error.errors?.some((e) => e.code === 'TIMESLOT_NOT_AVAILABLE')) {
      setErrorType('fullyBooked');
      goToStep('error');
      return;
    }

    toast({
      variant: 'destructive',
      description: translateError(error),
    });
  };

  const { mutate: rescheduleRecurringAppointment, isPending: isRecurringReschedulingLoading } = useRescheduleRecurringAppointment({
    options: {
      onSuccess: (data, variables) => {
        updateData('rescheduledAppointmentDetails', data);

        if (!data.booked_appointments || data.booked_appointments.length === 0) {
          const unavailableDates = data.notpossible_appointments ?? data.notBookedAppointments ?? [];

          if (unavailableDates.length === 0) {
            captureError(new Error('Recurring reschedule failed - returned no booked appointments and no unavailable dates'), {
              request: variables,
              response: data,
            });
          }

          setErrorType('rescheduleFailed');
          goToStep('error');
          return;
        }

        void captureAnalyticsEvent('rescheduling_complete', { appointment_ids: data.booked_appointments.map((app) => app.appointment_id) });
        invalidateSchedulingQueries();
        goToNextStep();
      },
      onError: (error) => {
        handleErrors(error);
      },
    },
  });

  const { mutate: rescheduleSingleAppointment, isPending: isSingleReschedulingLoading } = useRescheduleAppointment({
    options: {
      onSuccess: (data, variables) => {
        updateData('rescheduledAppointmentDetails', {
          booked_appointments: data.booked_appointments,
        });

        if (!data.booked_appointments || data.booked_appointments.length === 0) {
          captureError(new Error('Single reschedule failed - no appointment received'), {
            request: variables,
            response: data,
          });

          setErrorType('rescheduleFailed');
          goToStep('error');
          return;
        }

        invalidateSchedulingQueries();
        goToNextStep();
      },
      onError: (error) => {
        handleErrors(error);
      },
    },
  });

  const isLoading = isRecurringReschedulingLoading || isSingleReschedulingLoading;

  const handleConfirm = () => {
    if (isRecurring) {
      rescheduleRecurringAppointment({
        appointmentId: data.appointment!.appointment_id,
        studentId: data.appointment!.student_id,
        enrollmentId: data.appointment!.enrollment_id,
        targetDate: data.newDateAndTime,
        recurringSettings: {
          recurrencePattern: data.recurring.recurrencePattern!,
          endDate: recurrenceEndsAt!,
        },
        isPlan: false,
      });
    } else {
      rescheduleSingleAppointment({
        appointmentId: data.appointment!.appointment_id,
        studentId: data.appointment!.student_id,
        enrollmentId: data.appointment!.enrollment_id,
        targetDate: data.newDateAndTime,
      });
    }
  };

  const appointmentEnrollment = data.config.data?.students
    ? getStudentEnrollmentDetails(data.config.data.students, data.appointment.student_id, data.appointment.enrollment_id)
    : undefined;

  const conflictingAppointments: { date: string }[] =
    data.rescheduledAppointmentDetails.notpossible_appointments ?? data.rescheduledAppointmentDetails?.notBookedAppointments ?? [];
  const conflictingSessions = conflictingAppointments.map((session) => session.date);

  return (
    <>
      <DialogBody>
        <div className='mb-6'>
          <h3 className='text-heading-m-bold text-text-default-primary mb-2'>{t('title')}</h3>
          <p className='text-body-m-regular text-text-default-secondary'>{t('subheading')}</p>
        </div>

        {isRecurring && (
          <InfoMessage variant='info' description={t('info.recurringDayChange.description')} title={t('info.recurringDayChange.title')} className='mb-4' />
        )}
        {conflictingSessions.length > 0 && (
          <InfoMessage variant='warning' description={t('info.bookingConflict.description')} title={t('info.bookingConflict.title')}>
            <ul className='list-disc pl-5'>
              {conflictingSessions.map((session) => (
                <li key={session}>{formatDate(session)}</li>
              ))}
            </ul>
          </InfoMessage>
        )}
        <SessionSummary appointment={updatedAppointment} enrollment={appointmentEnrollment} />
      </DialogBody>

      <DialogFooter>
        <Button type='button' variant='text-secondary' onClick={goToPreviousStep} className='flex-1'>
          <ChevronLeft className='text-text-default-secondary size-5' />
          <span>{t('backButton.label')}</span>
        </Button>
        <Button type='button' onClick={handleConfirm} isLoading={isLoading} disabled={isLoading} className='flex-1'>
          {t(isLoading ? 'confirmButton.loadingLabel' : 'confirmButton.label')}
        </Button>
      </DialogFooter>
    </>
  );
};
